import {useEffect, useMemo, useRef, useState} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {DatePicker, MoneyField} from '@/components/common';
import {AllowanceScheduleTable} from '@/pages/components/tax-computation/sub/AllowanceScheduleTable';

export interface CommercialBuildRow {
    date: string | null;
    item: string;
    costPerBook: number;
    stampDuty: number;
    commisionLegalFees: number; // spelling per requirement
    ppeId?: number;
    propertyId?: number;
}

export type CommercialBuildSection = {
    priorLocationsProperties: CommercialBuildRow[];
    yearLocationsProperties: CommercialBuildRow[];
    valuePriorYear: number;
    valueThisYear: number;
    reducingBf: number;
    wdvBf?: number;
    addDuringTheYear?: number;
    annualAllowance?: number;
    totalAllowance?: number;
    salesProceed?: number;
    balancingCharge?: number;
    subtotalAfterAllowance?: number;
    subtotalAfterSales?: number;
    reducingValueCf?: number;
    fullyUsedInPriorYears?: number;
};

export const calcCommercialBuildNet = (r: CommercialBuildRow) => {
    const net = (Number(r.costPerBook) || 0) + (Number(r.stampDuty) || 0) + (Number(r.commisionLegalFees) || 0);
    return Math.max(0, net);
};

/** Aligns with AllowanceScheduleTable steps 3.2.5–3.2.7 */
export const computeCommercialAllowanceFields = (
    section: Pick<CommercialBuildSection, 'priorLocationsProperties' | 'yearLocationsProperties' | 'fullyUsedInPriorYears'>,
    reducingBf: number,
    leasehold = false,
    priorValueOverride?: number | null,
) => {
    if (Number(section.fullyUsedInPriorYears) === 1) {
        return {
            addDuringTheYear: 0,
            annualAllowance: 0,
            totalAllowance: 0,
        };
    }

    const priorNet = (section.priorLocationsProperties || []).reduce((s, r) => s + calcCommercialBuildNet(r), 0);
    const yearNet = (section.yearLocationsProperties || []).reduce((s, r) => s + calcCommercialBuildNet(r), 0);
    const v1Half = priorValueOverride != null && Number.isFinite(Number(priorValueOverride))
        ? Number(priorValueOverride) || 0
        : (leasehold ? priorNet : priorNet * 0.5);
    const v2Half = leasehold ? yearNet : yearNet * 0.5;
    const sum12 = v1Half + v2Half;
    const effectiveBf = Math.abs(Number(reducingBf) || 0);
    const addition = v2Half;
    const sum45 = effectiveBf + addition;
    const calculatedAllowance = Math.round(sum12 * 0.04);
    const totalAllowance = Math.min(calculatedAllowance, sum45);
    const subtotalAfterAllowance = sum45 - totalAllowance;

    return {
        addDuringTheYear: addition,
        annualAllowance: -totalAllowance,
        totalAllowance,
        subtotalAfterAllowance,
        reducingValueCf: Math.max(0, subtotalAfterAllowance),
    };
};

const applyCommercialAllowanceFields = (
    section: CommercialBuildSection | null,
    reducingBf: number | undefined,
    leasehold: boolean,
    priorValueOverride?: number | null,
): CommercialBuildSection | null => {
    if (!section) return null;
    // RX12 disposal / balancing charge fields come from backend; do not overwrite with the
    // normal 4% allowance formula (which ignores sales proceed and causes UI vs export mismatch).
    const hasBackendDisposal = Math.abs(Number(section.salesProceed) || 0) > 0
        || Math.abs(Number(section.balancingCharge) || 0) > 0
        || section.subtotalAfterSales != null;
    if (hasBackendDisposal) {
        return section;
    }
    const allowanceFields = computeCommercialAllowanceFields(
        section,
        Number(reducingBf) || 0,
        leasehold,
        priorValueOverride,
    );
    return {...section, ...allowanceFields};
};

const resolveScheduleBf = (
    section: CommercialBuildSection | null,
    propBf: number | undefined,
    nextYear: boolean,
    leasehold: boolean,
) => {
    const bfNum = Number(propBf);
    if (!Number.isNaN(bfNum) && bfNum !== 0) {
        return bfNum;
    }
    if (!section) {
        return 0;
    }
    if (nextYear || leasehold) {
        return Number(section.wdvBf ?? section.reducingBf) || 0;
    }
    return Number(section.reducingBf) || 0;
};

interface CommercialBuildScheduleTableProps {
    commercialRows: CommercialBuildSection | null;
    currency: string | null;
    onTotalsChange?: (totalNet: number) => void;
    commercialRowsChange?: (rows: CommercialBuildSection | null) => void;
    bf?: number;
    bfChange?: (bf: number) => void;
    nextYear?: boolean;
    leasehold?: boolean;
    periodFrom?: string | null;
    commercialPriorLabel?: string | null;
    commercialPriorValue?: number | null;
    onCommercialPriorOverrideChange?: (label: string | null, value: number | null) => void;
}

export function CommercialBuildScheduleTable({
                                                 commercialRows, currency,
                                                 onTotalsChange,
                                                 commercialRowsChange,
                                                 bf,
                                                 bfChange,
                                                 nextYear = false,
                                                 leasehold = false,
                                                 periodFrom = null,
                                                 commercialPriorLabel = null,
                                                 commercialPriorValue = null,
                                                 onCommercialPriorOverrideChange,
                                             }: CommercialBuildScheduleTableProps) {
    const asNegative = (n: number) => {
        const v = Number(n) || 0;
        return v > 0 ? -v : v;
    };

    const normalizeSection = (section: CommercialBuildSection | null): CommercialBuildSection | null => {
        if (!section) return null;
        return {
            ...section,
            priorLocationsProperties: (section.priorLocationsProperties || []).map((r) => ({
                ...r,
                stampDuty: Number(r.stampDuty) || 0,
                commisionLegalFees: asNegative(r.commisionLegalFees),
            })),
            yearLocationsProperties: (section.yearLocationsProperties || []).map((r) => ({
                ...r,
                stampDuty: Number(r.stampDuty) || 0,
                commisionLegalFees: asNegative(r.commisionLegalFees),
            })),
        };
    };

    // Maintain local editable state for stamp duty and commission/legal fees
    const [rows, setRows] = useState<CommercialBuildSection | null>(() => {
        const normalized = normalizeSection(commercialRows);
        const resolvedBf = resolveScheduleBf(normalized, bf, nextYear, leasehold);
        return applyCommercialAllowanceFields(
            normalized,
            resolvedBf,
            leasehold,
            commercialPriorValue,
        );
    });

    const commercialRowsChangeRef = useRef<CommercialBuildScheduleTableProps['commercialRowsChange']>(commercialRowsChange);
    const onTotalsChangeRef = useRef<CommercialBuildScheduleTableProps['onTotalsChange']>(onTotalsChange);
    const skipNotifyRef = useRef<boolean>(true);

    useEffect(() => {
        commercialRowsChangeRef.current = commercialRowsChange;
    }, [commercialRowsChange]);

    useEffect(() => {
        onTotalsChangeRef.current = onTotalsChange;
    }, [onTotalsChange]);

    useEffect(() => {
        skipNotifyRef.current = true;
        const normalized = normalizeSection(commercialRows);
        const resolvedBf = resolveScheduleBf(normalized, bf, nextYear, leasehold);
        setRows(applyCommercialAllowanceFields(
            normalized,
            resolvedBf,
            leasehold,
            commercialPriorValue,
        ));
    }, [commercialRows, bf, leasehold, nextYear, commercialPriorValue]);

    useEffect(() => {
        if (skipNotifyRef.current) {
            skipNotifyRef.current = false;
            return;
        }
        commercialRowsChangeRef.current?.(rows || null);
    }, [rows]);

    const calcNet = calcCommercialBuildNet;

    const parseDMY = (value?: string | null): Date | null => {
        if (!value) return null;
        const m = /^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/.exec(value);
        if (!m) return null;
        const d = Number(m[1]);
        const mo = Number(m[2]) - 1;
        const y = Number(m[3]);
        const dt = new Date(y, mo, d);
        if (dt.getFullYear() === y && dt.getMonth() === mo && dt.getDate() === d) {
            return dt;
        }
        return null;
    };

    const hasPriorDateBeforePeriod = useMemo(() => {
        const periodDate = parseDMY(periodFrom);
        if (!periodDate) return false;
        const prior = rows?.priorLocationsProperties || [];
        return prior.some((r) => {
            const rowDate = parseDMY(r.date);
            return rowDate !== null && rowDate.getTime() < periodDate.getTime();
        });
    }, [rows, periodFrom]);

    const totalNet = useMemo(() => {
        const prior = rows?.priorLocationsProperties || [];
        const year = rows?.yearLocationsProperties || [];
        return [...prior, ...year].reduce((s, r) => s + calcNet(r), 0);
    }, [rows]);

    const priorGroupTotalNet = useMemo(() => {
        const prior = rows?.priorLocationsProperties || [];
        return (prior || []).reduce((s, r) => s + calcNet(r), 0);
    }, [rows]);

    const yearGroupTotalNet = useMemo(() => {
        const year = rows?.yearLocationsProperties || [];
        return (year || []).reduce((s, r) => s + calcNet(r), 0);
    }, [rows]);

    const scheduleBf = resolveScheduleBf(rows, bf, nextYear, leasehold);

    useEffect(() => {
        onTotalsChangeRef.current?.(totalNet);
    }, [totalNet]);

    const hasAny = (rows?.priorLocationsProperties?.length || 0) > 0 || (rows?.yearLocationsProperties?.length || 0) > 0;
    if (!hasAny) return null;

    const handleFullyUsedChange = (v: boolean) => {
        setRows((prev) => {
            if (!prev) return prev;
            const nextSection: CommercialBuildSection = {
                ...prev,
                fullyUsedInPriorYears: v ? 1 : 0,
            };
            return applyCommercialAllowanceFields(
                nextSection,
                resolveScheduleBf(nextSection, bf, nextYear, leasehold),
                leasehold,
                commercialPriorValue,
            );
        });
    };

    const handleChange = (group: 'prior' | 'year', idx: number, patch: Partial<CommercialBuildRow>) => {
        setRows((prev) => {
            if (!prev) return prev;
            const listKey = group === 'prior' ? 'priorLocationsProperties' : 'yearLocationsProperties';
            const nextList = (prev[listKey] || []).map((r, i) => (i === idx ? {...r, ...patch} : r));
            const nextSection = {...prev, [listKey]: nextList};
            return applyCommercialAllowanceFields(
                nextSection,
                resolveScheduleBf(nextSection, bf, nextYear, leasehold),
                leasehold,
                commercialPriorValue,
            );
        });
    };

    const renderRows = (rows: CommercialBuildRow[], group: 'prior' | 'year', nextYear?: boolean) => {
        const groupTotalNet = (rows || []).reduce((s, r) => s + calcNet(r), 0);
        return (
            <>
                {/*/!* One header shared by two bodies *!/*/}
                <TableHead>
                    {(leasehold && group === 'prior') && (
                        <TableRow>
                            <TableCell colSpan={6} sx={{py: 1}}>
                                <Typography variant="body2" sx={{fontWeight: 600}}>
                                    LEASEHOLD IMPROVEMENT
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                    <TableRow>
                        <TableCell colSpan={2} sx={{py: 1}}>
                            {!leasehold ? (
                                <Typography variant="body2" sx={{fontWeight: 600}}>
                                    {group === 'prior'
                                        ? 'Properties obtained in prior years'
                                        : 'Properties obtained this year'}
                                    <br/>
                                    Locations of properties
                                </Typography>
                            ) : (
                                <Typography variant="body2" sx={{fontWeight: 600}}>
                                    {group === 'prior' ? 'Prior years' : 'This year'}
                                    <br/>
                                    Locations of leasehold improvement
                                </Typography>
                            )}

                        </TableCell>
                        <TableCell align="center">Cost per book</TableCell>
                        <TableCell align="center">{!leasehold ? 'Stamp duty' : ''}</TableCell>
                        <TableCell align="center">{!leasehold ? 'Commission and legal fees' : ''}</TableCell>
                        <TableCell align="center">Net value</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(rows || []).map((row, idx) => {
                        const netValue = calcNet(row);
                        const onChange = (p: Partial<CommercialBuildRow>) => handleChange(group, idx, p);
                        return (

                            <TableRow key={`${group}-${idx}`}>
                                {/* Date (read-only) */}
                                <TableCell>
                                    <DatePicker align='left' value={row.date} onChange={() => {
                                    }} display size="small"/>
                                </TableCell>
                                {/* Item (read-only) */}
                                <TableCell>
                                    <Typography variant="body2">
                                        {row.item}
                                    </Typography>
                                </TableCell>
                                {/* Cost per book (read-only money) */}
                                <TableCell align="center">
                                    <MoneyField value={row.costPerBook} editable={false} currencySymbol={currency || ''}
                                                showCents={false} align="center"/>
                                </TableCell>
                                {/* Stamp duty (editable money) */}
                                <TableCell align="center" sx={{py: 0}}>
                                    {!leasehold && (
                                        <Box sx={{width: '55%', mx: 'auto'}}>
                                            <MoneyField
                                                value={row.stampDuty}
                                                editable
                                                tiny
                                                currencySymbol={currency || ''}
                                                showCents={false}
                                                align="center"
                                                onChangeNumber={(n) => onChange({stampDuty: Number(n) || 0})}
                                            />
                                        </Box>
                                    )}
                                </TableCell>
                                {/* Commission and legal fees (editable money) */}
                                <TableCell align="center" sx={{py: 0}}>
                                    {!leasehold && (
                                        <Box sx={{width: '55%', mx: 'auto'}}>
                                            <MoneyField
                                                value={row.commisionLegalFees}
                                                editable
                                                tiny
                                                currencySymbol={currency || ''}
                                                showCents={false}
                                                align="center"
                                                onChangeNumber={(n) => onChange({commisionLegalFees: asNegative(n)})}
                                            />
                                        </Box>
                                    )}
                                </TableCell>
                                {/* Net value (read-only money, >=0 else 0) */}
                                <TableCell align="center">
                                    <MoneyField value={netValue} editable={false} currencySymbol={currency || ''}
                                                showCents={false}
                                                align="center"/>
                                </TableCell>
                            </TableRow>
                        );
                    })}

                    {/* Totals row: only Net value total */}
                    <TableRow sx={{backgroundColor: '#f5f5f5'}}>
                        <TableCell colSpan={5}/>
                        <TableCell align="center">
                            <MoneyField value={groupTotalNet} editable={false}
                                        currencySymbol={currency || ''} showCents={false} align="center"/>
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell colSpan={8} sx={{py: 0.25, verticalAlign: 'middle'}}/>
                    </TableRow>
                </TableBody>
            </>
        );
    };

    return (
        <Box>
            {rows && (
                <>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography variant="h6" sx={{fontWeight: 600}}>
                                        COMMERCIAL BUILDING ALLOWANCE SCHEDULE
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        {(rows.priorLocationsProperties || []).length > 0 &&
                            renderRows(rows.priorLocationsProperties || [], 'prior')}
                        {(rows.yearLocationsProperties || []).length > 0 &&
                            renderRows(rows.yearLocationsProperties || [], 'year')}
                    </Table>

                    <AllowanceScheduleTable
                        currency={currency}
                        valuePriorYear={priorGroupTotalNet}
                        valueThisYear={yearGroupTotalNet}
                        reducingBf={rows.reducingBf}
                        bf={scheduleBf}
                        bfChange={bfChange}
                        nextYear={nextYear}
                        leasehold={leasehold}
                        backendAnnualAllowance={rows.annualAllowance}
                        backendTotalAllowance={rows.totalAllowance}
                        backendAddDuringTheYear={rows.addDuringTheYear}
                        backendSalesProceed={rows.salesProceed}
                        backendBalancingCharge={rows.balancingCharge}
                        backendSubtotalAfterAllowance={rows.subtotalAfterAllowance}
                        backendSubtotalAfterSales={rows.subtotalAfterSales}
                        backendReducingValueCf={rows.reducingValueCf}
                        showPriorYearFullyUsed={hasPriorDateBeforePeriod}
                        priorYearFullyUsed={rows.fullyUsedInPriorYears === 1}
                        priorYearFullyUsedChange={handleFullyUsedChange}
                        commercialPriorLabel={commercialPriorLabel}
                        commercialPriorValue={commercialPriorValue}
                        onCommercialPriorOverrideChange={onCommercialPriorOverrideChange}
                    />
                </>
            )}
        </Box>
    );
}
